/* eslint-disable react-hooks/rules-of-hooks */
import { MutableRefObject, useState, useEffect, useMemo } from "react";
import { ExecutionMethod } from "api/enums";
import { SecurityTag, SecurityTypeCode } from "api/holdings/types";
import { useGetSecurityDetails } from "api/holdings/useGetSecurityDetails";
import { useGetContactInfo } from "api/initial/useGetContactInfo";
import { useGetBuyData } from "api/trading/useGetBuyData";
import { useTrade } from "api/trading/useTrade";
import { ReactComponent as InfoIcon } from "assets/information-circle.svg";
import {
  Checkbox,
  PortfolioSelect,
  DownloadableDocument,
  Input,
  LabeledDiv,
  Select,
  LoadingIndicator,
  KIDTooltip,
} from "components/index";
import { LabeledDivFlex } from "components/LabeledDiv/LabeledDivFlex";
import { PrimaryActionButton } from "components/TradingButtons/PrimaryActionButton";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { useTradingButtons } from "hooks/useTradingButtons";
import { useGetContractIdData } from "providers/ContractIdProvider";
import { useKeycloak } from "providers/KeycloakProvider";
import { Tooltip } from "react-tooltip";
import { cblPortalGroupTag } from "services/permissions/usePermission";
import { getBackendTranslation } from "utils/backTranslations";
import { handleNumberInputEvent, handleNumberPasteEvent, isValidInputByStep } from "utils/input";
import { round, roundDown } from "utils/number";
import {
  getBalanceAmountByCurrency,
  getBlockSizeErrorTooltip,
  getBlockSizeMinTradeAmount
} from "utils/trading";
import { PageContext, ButtonAction, ButtonVariant } from "utils/trading/enums";
import { addProtocolToUrl } from "utils/url";
import { isProfessionalClassification } from "utils/userClassification";
import { useGetAvailableBalancesExperimental } from "../../../api/overview/useGetAvailableBalancesExperimental";
import {useGetFinancialProfile} from "../../../api/trading/useGetFinancialProfile";
import { filterJustBrokerage } from "../../../services/permissions/usePermission";
import { useGetConfirmationCheckInfo } from "../useGetConfirmationCheckInfo";
import { useOrderTypeFields, EOrderType } from "../useOrderTypeFields";
import { useTradablePortfolioSelect } from "../useTradablePortfolioSelect";
import DisclaimerBlock from "./DisclaimerBlock";
import { useGetBuyTradeType } from "./useGetBuyTradeType";

export interface BuyModalInitialData {
  id: number;
  securityCode: string | null;
}

interface BuyModalProps extends BuyModalInitialData {
  modalInitialFocusRef: MutableRefObject<null>;
  onClose: () => void;
}

export const isSecurityTypeFund = (
  securityType: SecurityTypeCode | undefined
) => securityType === SecurityTypeCode.COLLECTIVE_INVESTMENT_VEHICLE;

const getTradeType = (securityType: SecurityTypeCode | undefined) =>
  isSecurityTypeFund(securityType) ? "subscription" : "buy";

const FALLBACK_DECIMAL_COUNT = 2;

export const BuyModalContent = ({ 
  modalInitialFocusRef,
  onClose,
  id: securityId,
  securityCode,
}: BuyModalProps) => {
  const [submitting, setSubmitting] = useState(false);
  const { t, i18n } = useModifiedTranslation();
  const { selectedContactId } = useGetContractIdData();
  const { data: { portfolios } = { portfolios: [] } } = useGetContactInfo(
    false,
    selectedContactId
  );
  const { setPortfolioId, portfolioOptions, portfolioId } =
    useTradablePortfolioSelect();

  const filteredPortfolioOptions = filterJustBrokerage(portfolioOptions);
  const selectedPortfolioId = portfolioId
      || (filteredPortfolioOptions.length === 1 ? filteredPortfolioOptions[0]?.id : undefined);
  const selectedPortfolio = useMemo(() => {
    return portfolios.find((p) => p.id === selectedPortfolioId);
  }, [portfolios, selectedPortfolioId]);


  const { data: security, loading: loadingSecurity } = useGetSecurityDetails(
      securityCode?.toString() ?? securityId?.toString(),
      selectedPortfolio?.currency?.securityCode,
      !!securityCode
  );

  const { loading: loadingCash, data: portfolioData } = useGetBuyData(
    selectedPortfolioId,
    security?.currency?.securityCode
  );

  const securityTypeCode = security?.type?.code;


  const { isTradeInUnits, canToggleTradeType, setIsTradeInUnits } =
    useGetBuyTradeType(security?.tagsAsSet, securityTypeCode);

  const [input, setInput] = useState<string>("");
  const inputAsNr = input ? Number(input) : 0;


  const securityAmountDecimalCount =
    security?.amountDecimalCount !== undefined
      ? security.amountDecimalCount
      : FALLBACK_DECIMAL_COUNT;
  const securityCurrencyAmountDecimalCount =
    security?.currency?.amountDecimalCount !== undefined
      ? security?.currency?.amountDecimalCount
      : FALLBACK_DECIMAL_COUNT;
  const portfolioCurrencyAmountDecimalCount =
    portfolioData?.currency?.amountDecimalCount !== undefined
      ? portfolioData?.currency?.amountDecimalCount
      : FALLBACK_DECIMAL_COUNT;
  const inputBlockSize = isTradeInUnits
    ? securityAmountDecimalCount
    : portfolioCurrencyAmountDecimalCount;

  const securityName =
    security !== undefined
      ? getBackendTranslation(
          security?.name ?? '',
          security?.namesAsMap,
          i18n.language
        )
      : undefined;

  const securityToPortfolioFxRate = 1 //security?.fxRate || 1;
  const securityPrice = security?.latestMarketData?.price;

  const securityPriceInPfCurrency =
    securityPrice !== undefined
      ? round(
          securityPrice * securityToPortfolioFxRate,
          portfolioCurrencyAmountDecimalCount
        )
      : undefined;

  const unitsToBuyFromTradeAmount =
    securityPriceInPfCurrency !== undefined
      ? inputAsNr / (securityPriceInPfCurrency || 1)
      : undefined;

  const unitsToBuy = isTradeInUnits
    ? roundDown(inputAsNr, securityAmountDecimalCount)
    : unitsToBuyFromTradeAmount !== undefined
    ? roundDown(unitsToBuyFromTradeAmount, securityAmountDecimalCount)
    : undefined;

    const isSubScPortalGroup = [
      SecurityTypeCode.DEBT_INSTRUMENT,
      SecurityTypeCode.EQUITY,
    ].includes(securityTypeCode as SecurityTypeCode) && security?.tagsAsSet.includes(SecurityTag.SUBSCR_PORTAL_GROUP);

    const { orderType, setOrderType, orderTypeOptions,
      orderExpiration, setOrderExpiration, orderExpirationOptions
      , unitPrice, setUnitPrice, getFormattedOrderTypeFields,
        isOrderTypeFieldsVisible, setConfirmationIsChecked, isConfirmationChecked,
        getDisclaimerKeys } = useOrderTypeFields();

    const [stringUnitPrice, setStringUnitPrice] = useState<string>("");

    useEffect(() => {
      setUnitPrice(+stringUnitPrice);
    }, [setUnitPrice, stringUnitPrice]);

  let estimatedTradeAmountInPfCurrency = 
    unitsToBuy !== undefined && securityPrice !== undefined
      ? round(
          unitsToBuy * (securityPrice * securityToPortfolioFxRate),
          portfolioCurrencyAmountDecimalCount
        )
      : undefined;


  //------estimateTradeAmount variations -----
    if (estimatedTradeAmountInPfCurrency && security?.type?.code === SecurityTypeCode.COLLECTIVE_INVESTMENT_VEHICLE) {
      estimatedTradeAmountInPfCurrency = inputAsNr;
    }
  
    if (securityPrice && estimatedTradeAmountInPfCurrency && security?.type?.code === SecurityTypeCode.DEBT_INSTRUMENT) {
      if (orderType?.id === EOrderType.LIMIT_ORDER) {
        estimatedTradeAmountInPfCurrency = round(
          (+inputAsNr * +(unitPrice || 0)) / 100,
          portfolioCurrencyAmountDecimalCount
        );
      } else {
        estimatedTradeAmountInPfCurrency = round(
          (+inputAsNr * +securityPrice) / 100,
          portfolioCurrencyAmountDecimalCount
        );
      }
    }
  
    if (securityTypeCode && securityPrice && estimatedTradeAmountInPfCurrency 
      && orderType?.id === EOrderType.LIMIT_ORDER
      && [SecurityTypeCode.EQUITY, SecurityTypeCode.ETF].includes(securityTypeCode)) {
        estimatedTradeAmountInPfCurrency = round(
          (+inputAsNr * +(unitPrice || 0)),
          portfolioCurrencyAmountDecimalCount
        );
    }


  const estimatedTradeAmountInSecurityCurrency =
    unitsToBuy !== undefined && securityPrice !== undefined
      ? round(unitsToBuy * securityPrice, securityCurrencyAmountDecimalCount)
      : undefined;

  const { handleTrade: handleBuy } = useTrade({
    isInSubscrPortalGroup: isSubScPortalGroup,
    unitPrice: unitPrice,
    tradeType: getTradeType(securityTypeCode),
    portfolio: selectedPortfolio || portfolios[0],
    securityName: securityName || "-",
    units: isTradeInUnits ? unitsToBuy : undefined,
    tradeAmount: !isTradeInUnits
      ? estimatedTradeAmountInSecurityCurrency
      : undefined,
    securityCode: security?.securityCode || "",
    executionMethod: 
    security?.type?.code === SecurityTypeCode.COLLECTIVE_INVESTMENT_VEHICLE ? ExecutionMethod.GROSS_TRADE_AMOUNT :
      isTradeInUnits
        ? ExecutionMethod.UNITS
        : ExecutionMethod.NET_TRADE_AMOUNT,
    ...getFormattedOrderTypeFields(security, isConfirmationChecked)
  });

  const accountName = selectedPortfolio?.accounts[0].accountName.split('-')[0];
  const externalId = selectedPortfolio?.shortName.split('-')[0];

  const {data: financialProfile} = useGetFinancialProfile(externalId);

  const { balances } = useGetAvailableBalancesExperimental();

  const tradingBalances = balances[accountName || '']?.trading || [];
  const securityCurrency = security?.currency?.securityCode;

  const availableCash = getBalanceAmountByCurrency(tradingBalances, securityCurrency);
  const portfolioCurrency = securityCurrency;

  const showsReservationFee =
    !isSubScPortalGroup &&
    orderType?.id !== EOrderType.LIMIT_ORDER &&
    !!securityTypeCode &&
    [SecurityTypeCode.EQUITY, SecurityTypeCode.ETF].includes(securityTypeCode);

  const requiredCash =
    (estimatedTradeAmountInPfCurrency || 0) * (showsReservationFee ? 1.1 : 1);

  const insufficientCash = (availableCash || 0) < requiredCash; // less than trying to buy for (incl. 10% reservation fee when applicable)

  const { readonly } = useKeycloak();

  const tradeAmountTooltip = () => {
    if (isSubScPortalGroup) {
      return undefined
    }

    if (security?.type?.code === SecurityTypeCode.COLLECTIVE_INVESTMENT_VEHICLE) {
      return t("tradingModal.approximateTradeAmountTooltip");
    }

    if ([
      SecurityTypeCode.EQUITY,
      SecurityTypeCode.ETF,
      SecurityTypeCode.DEBT_INSTRUMENT
    ].includes(security?.type?.code as SecurityTypeCode)) {
      return t("tradingModal.approximateTradeAmountTooltip2");
    }

    // if (security?.type?.code === SecurityTypeCode.EQUITY 
    //   || security?.type?.code === SecurityTypeCode.ETF) {

    //     if (orderType?.id === EOrderType.LIMIT_ORDER) {
    //       return undefined;
    //     }
        
    //     if (unitsToBuy !== undefined &&
    //         security !== undefined &&
    //         security &&
    //         portfolioCurrency !== undefined) {
    //           return getTradeAmountTooltip(
    //             unitsToBuy,
    //             security,
    //             security.fxRate,
    //             portfolioCurrency,
    //             i18n.language,
    //             t,
    //           )
    //         } 
    // }

    return undefined
  }

  //min trade amount allowed to trade in this security
  //based on its block size only
  const blockSizeMinTradeAmountInPfCurrency =
    securityPrice !== undefined
      ? round(
          getBlockSizeMinTradeAmount(
            securityAmountDecimalCount,
            securityPrice
          ) * securityToPortfolioFxRate,
          portfolioCurrencyAmountDecimalCount
        )
      : undefined;

  const blockSizeTradeAmountError =
    !isTradeInUnits &&
    inputAsNr > 0 &&
    security !== undefined &&
    security &&
    blockSizeMinTradeAmountInPfCurrency !== undefined &&
    portfolioCurrency !== undefined &&
    estimatedTradeAmountInPfCurrency !== undefined &&
    blockSizeMinTradeAmountInPfCurrency > estimatedTradeAmountInPfCurrency //input is lower than min allowed trade amount
      ? getBlockSizeErrorTooltip(
          blockSizeMinTradeAmountInPfCurrency,
          security,
          security.fxRate,
          portfolioCurrency,
          i18n.language,
          t,
          true
        )
      : undefined;

  useEffect(() => {
    //when switching between amount and trade amount
    //we must make sure the input is rounded to the allowed
    //amount of decimals
    setInput((currInput) =>
      currInput ? round(parseFloat(currInput), inputBlockSize).toString() : ""
    );
  }, [inputBlockSize]);

  const loading = loadingCash || loadingSecurity;

  const securityType: SecurityTypeCode | undefined = securityTypeCode;
  const isCBL: boolean | undefined = security?.profile?.attribute?.stringValue === "913";
  const securityClassType: string = security?.classType1?.value || '';

  const { keyPrefix, translationDisclaimerKeys } = getDisclaimerKeys(securityClassType, securityType, isCBL, financialProfile?.contacts[0]?.classification?.code);

  const confirmationCheckInfo = useGetConfirmationCheckInfo(selectedPortfolio, security);
  
  const requiresConfirmationCheck = !!confirmationCheckInfo;

  const minimalNominalValue = securityTypeCode === SecurityTypeCode.DEBT_INSTRUMENT 
    && security?.minTradeAmount ? security.minTradeAmount : undefined;

  const stepNominalValue = securityTypeCode === SecurityTypeCode.DEBT_INSTRUMENT 
    && security?.blockSize ? security.blockSize : undefined;

  const priceAsInteger = securityTypeCode && [
    SecurityTypeCode.EQUITY, 
    SecurityTypeCode.ETF,
  ].includes(securityTypeCode);

  const disableBuyButton = () => {
    return (
      loading ||
      inputAsNr === 0 ||
      insufficientCash ||
      !!blockSizeTradeAmountError ||
      readonly ||
      !selectedPortfolio ||
      (requiresConfirmationCheck && !isConfirmationChecked) ||
      submitting ||
      (priceAsInteger && Number.isInteger(inputAsNr) === false) ||
      !!(minimalNominalValue && inputAsNr < minimalNominalValue) ||
      !!(stepNominalValue && minimalNominalValue && inputAsNr >= minimalNominalValue && (inputAsNr % stepNominalValue) !== 0)
    );
  };

  // comment for now, not sure if we need it
  // const amountInputLabel = () => {
  //     switch (securityTypeCode) {
  //         case SecurityTypeCode.EQUITY:
  //         case SecurityTypeCode.ETF:
  //             return t("tradingModal.unitsInputLabel");
  //         case SecurityTypeCode.DEBT_INSTRUMENT:
  //             return minimalNominalValue 
  //               ? `${t("tradingModal.valueInputLabel")} (${t('tradingList.minimalNominalValue')}:${minimalNominalValue})` 
  //               : t("tradingModal.valueInputLabel");
  //         default:
  //             return t("tradingModal.tradeAmountSimpleInputLabel");
  //     }
  // };
 
  const amountInputLabel = () => {
    switch (securityTypeCode) {
        case SecurityTypeCode.EQUITY:
            return t("transactionsPage.numberOfShares");
        case SecurityTypeCode.COLLECTIVE_INVESTMENT_VEHICLE:
          return t("transactionsPage.tradeAmount");
        case SecurityTypeCode.DEBT_INSTRUMENT:
            return minimalNominalValue 
              ? `${t("transactionsPage.nominalValue")} (${t('tradingList.minimalNominalValue')}:${minimalNominalValue})` 
              : t("transactionsPage.nominalValue");
        default:
            return t("transactionsPage.quantity");
    }
};

  // Use trading buttons hook to determine the correct button action
  // Always call the hook (React rules), but pass undefined if security not loaded yet
  const tradingButtonsConfig = useTradingButtons(
    security
      ? {
          security: {
            id: security.id.toString(),
            securityTypeCode: security.type?.code || '',
            class1Name: security.classType1?.value || '',
            currencyCode: security.currency.securityCode,
            tagsAsList: security.tagsAsSet || [],
            coupon: security.coupon,
            maturityDate: security.maturityDate || undefined,
          },
          pageContext: PageContext.SECURITY_DETAILS,
          availableCash: availableCash,
          isLoading: submitting,
        }
      : undefined
  );

  // Get the primary button config (BUY or FILL_THE_TEST)
  const buttonConfig = tradingButtonsConfig.primary;

  // Show loading button while data is loading
  const actionButton = loading ? (
    <PrimaryActionButton
      action={ButtonAction.BUY}
      label=""
      variant={ButtonVariant.TRANSPARENT}
      size="md"
      isLoading={true}
      disabled={true}
      onClick={(e) => {
        e.preventDefault();
      }}
    />
  ) : buttonConfig ? (
    <PrimaryActionButton
      action={buttonConfig.action}
      label={buttonConfig.label}
      variant={buttonConfig.variant}
      size="md"
      isLoading={submitting}
      disabled={disableBuyButton()}
      onClick={async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const response = await handleBuy();
        if (response) {
          onClose();
        }
        setSubmitting(false);
      }}
    />
  ) : null;

  const hasMarketData = !!security?.latestMarketData;

  const handleInputErrors = (): string => {
    if (selectedPortfolioId === undefined) {
      return "";
    }

    if (input && inputAsNr > 0) {
      if (priceAsInteger && Number.isInteger(inputAsNr) === false) {
        return t("tradingModal.tradeAmountIntegerError");
      }

      if (insufficientCash && !loading) {
        return t("tradingModal.insufficientCashError");
      }

      if (minimalNominalValue && inputAsNr < minimalNominalValue) {
        return t("tradingModal.minimalNominalValueError");
      }

      if (stepNominalValue && minimalNominalValue && inputAsNr >= minimalNominalValue && !isValidInputByStep(inputAsNr, stepNominalValue) ) {
        return t("tradingModal.stepNominalValueError", {
          step: stepNominalValue,
        });
      }

    }

    return "";
  };

  const additionalCostsLabel = securityTypeCode && securityTypeCode === SecurityTypeCode.DEBT_INSTRUMENT ? t("tradingModal.additionalCostsDEBT") 
    : securityTypeCode && securityTypeCode === SecurityTypeCode.COLLECTIVE_INVESTMENT_VEHICLE 
      && security?.tagsAsSet.includes(cblPortalGroupTag)
        ? '' 
        : t("tradingModal.additionalCosts");

  const hideTooltipVisible = [
    SecurityTypeCode.COLLECTIVE_INVESTMENT_VEHICLE,
    SecurityTypeCode.ETF
  ].includes(securityTypeCode as SecurityTypeCode)
    && security?.tagsAsSet.includes("PROF-Yes");

  const hideKidAndProspect = isProfessionalClassification(
    financialProfile?.contacts[0]?.classification?.code
  );



  return (
      <div className="grid gap-2 min-w-[min(84vw,_375px)]">
        {securityName && (
            <LabeledDiv
                label={t("tradingModal.securityName")}
                className="max-w-24ch text-2xl font-semibold"
            >
              {securityName}
            </LabeledDiv>
        )}
        {loadingSecurity && <LoadingIndicator size="sm"/>}
        {security?.url2 && !hideKidAndProspect && (
            <div className="flex items-center w-fit">
              <DownloadableDocument
                  url={addProtocolToUrl(security?.url2)}
                  label={t("tradingModal.kiid")}
              />
              {!hideTooltipVisible && <KIDTooltip />}
            </div>
        )}

        {(!hasMarketData) && <div>{t("tradingModal.noMarketData")}</div>}

        {hasMarketData &&<PortfolioSelect
            portfolioOptions={filteredPortfolioOptions}
            portfolioId={selectedPortfolioId}
            onChange={(newPortfolio) => setPortfolioId(newPortfolio.id)}
            label={t("tradingModal.portfolio")}
            error={!selectedPortfolioId ? t("tradingModal.selectPortfolioError") : ""}
        />}
        {hasMarketData && !loadingCash && (
            <LabeledDiv
                label={t("tradingModal.availableCash")}
                className="text-xl font-semibold text-gray-700"
            >
              {availableCash !== undefined && portfolioCurrency !== undefined
                  ? t("numberWithCurrency", {
                    value: availableCash,
                    currency: portfolioCurrency,
                  })
                  : "-"}
            </LabeledDiv>
        )}
        {loadingCash && <LoadingIndicator size="xs"/>}
        {hasMarketData && <Input
            maxLength={15}
            disabled={!selectedPortfolioId}
            ref={modalInitialFocusRef}
            value={input}
            onChange={(event) => {
              handleNumberInputEvent(event, setInput, 0, undefined, inputBlockSize);
            }}
            onPaste={(event) => {
              handleNumberPasteEvent(event, setInput, 0, undefined, inputBlockSize);
            }}
            onKeyDown={(event) => {
              if (priceAsInteger && (event.key === '.' || event.key === ',')) {
                event.preventDefault();
              }

              const input = event.target as HTMLTextAreaElement;
              const value = input.value;
              const key = event.key;
          
              //prevent round up decimals
              if (!isNaN(+key) && key !== ' ') {
                const selectionStart = input.selectionStart;
                const selectionEnd = input.selectionEnd;
        
                // Check if the entire input value is selected
                if (selectionStart === 0 && selectionEnd === value.length) {
                    return; // Allow number input
                }

                const decimalSeparator = value.includes('.') ? '.' : (value.includes(',') ? ',' : null);
        
                if (decimalSeparator) {
                    const parts = value.split(decimalSeparator);
                    if (parts[1] && parts[1].length >= inputBlockSize) {
                        event.preventDefault();
                    }
                }
              }

            }}
            label={amountInputLabel()}
            type="text"
            error={handleInputErrors()}
        />}

        {!isSubScPortalGroup && hasMarketData && isOrderTypeFieldsVisible(securityTypeCode) && (
            <>
              <div className="grid grid-flow-col gap-2">
                <LabeledDiv
                    label={t("tradingModal.orderType")}
                    className="text-xl font-semibold text-gray-700"
                >
                  <Select
                      value={orderType}
                      options={orderTypeOptions}
                      onChange={setOrderType}
                  />
                </LabeledDiv>

                <LabeledDiv
                    label={t("tradingModal.orderExpiration")}
                    className="text-xl font-semibold text-gray-700"
                >
                  <Select
                      value={orderExpiration}
                      options={orderExpirationOptions}
                      onChange={setOrderExpiration}
                      disabled={orderType?.id === EOrderType.MARKET_ORDER}
                  />
                </LabeledDiv>
              </div>

              {orderType?.id === EOrderType.LIMIT_ORDER &&
                  <LabeledDiv
                  label={securityTypeCode && [SecurityTypeCode.EQUITY, SecurityTypeCode.ETF].includes(securityTypeCode) 
                    ? t("tradingModal.limitPrice") : t("tradingModal.percentageOfNominalValue")}
                  className="text-xl font-semibold text-gray-700"
                >
                <Input
                    type="text"
                    value={stringUnitPrice}
                    onChange={(event) => {
                      handleNumberInputEvent(event, setStringUnitPrice, 0, undefined, 3);
                    }}
                    onPaste={(event) => {
                      handleNumberPasteEvent(event, setStringUnitPrice, 0, undefined, 3);
                    }}
                    onKeyDown={(event) => {
                      const input = event.target as HTMLTextAreaElement;
                      const value = input.value;
                      const key = event.key;
                  
                      //prevent round up decimals
                      if (!isNaN(+key) && key !== ' ') {
                        const selectionStart = input.selectionStart;
                        const selectionEnd = input.selectionEnd;
                
                        // Check if the entire input value is selected
                        if (selectionStart === 0 && selectionEnd === value.length) {
                            return; // Allow number input
                        }
            
                        const decimalSeparator = value.includes('.') ? '.' : (value.includes(',') ? ',' : null);
                
                        if (decimalSeparator) {
                            const parts = value.split(decimalSeparator);
                            if (parts[1] && parts[1].length >= 3) {
                                event.preventDefault();
                            }
                        }
                      }
                    }}
                    label={""}
                    maxLength={15}
                />
              </LabeledDiv>
              }
            </>
        )}

          {hasMarketData && canToggleTradeType && (
              <>
                  <div
                      className="flex overflow-hidden font-medium leading-5 bg-gray-50 rounded-md divide-x ring-1 shadow-sm pointer-events-auto select-none divide-slate-400/20 text-[0.8125rem] ring-slate-700/10">
                <button
                    className={`text-center cursor-pointer py-2 px-4 flex-1 ${
                        isTradeInUnits ? "bg-gray-200" : ""
                    }`}
                    onClick={() => setIsTradeInUnits(true)}
                >
                  {t("tradingModal.unitsButtonLabel")}
                </button>

                <button
                    className={`text-center cursor-pointer py-2 px-4 flex-1 ${
                        !isTradeInUnits ? "bg-gray-200" : ""
                    }`}
                    onClick={() => setIsTradeInUnits(false)}
                >
                  {t("tradingModal.tradeAmountButtonLabel")}
                </button>
              </div>
            </>
        )}

        {hasMarketData && <hr className="my-2"/>}
        {hasMarketData && <div className="flex flex-col gap-4 items-stretch ">
              <div>
                  <LabeledDivFlex
                      alignText="left"
                      tooltipContent={tradeAmountTooltip()}
                      id="buyOrderModal-tradeAmount"
                      label={t("tradingModal.approximateTradeAmount")}
                      className="text-2xl font-semibold"
                  >
                      {portfolioCurrency !== undefined &&
                      estimatedTradeAmountInPfCurrency !== undefined
                          ? `${t("number", {
                              value: estimatedTradeAmountInPfCurrency,
                              minimumFractionDigits: 2
                          })} ${portfolioCurrency} `
                          : "-"}
                  </LabeledDivFlex>
                
                  {securityTypeCode && orderType?.id !== EOrderType.LIMIT_ORDER 
                    && [SecurityTypeCode.EQUITY, SecurityTypeCode.ETF].includes(securityTypeCode) && !isSubScPortalGroup &&
                  <div className="mb-1">
                    <div className="flex flex-col items-start">
                      <div className="flex flex-row gap-x-1">
                        <div className="flex items-center text-sm font-normal">
                          <span>
                            {t("tradingModal.reservationFee")}
                            :
                          </span>
                          <span className="font-semibold">&nbsp;&nbsp;
                          {t("number", {
                                value: estimatedTradeAmountInPfCurrency ? 
                                (estimatedTradeAmountInPfCurrency * 0.10).toFixed(2)
                                : 0,
                            })} {portfolioCurrency}
                            &nbsp;&nbsp;
                          </span>
                          <InfoIcon data-tooltip-id="tooltip-reservation-fee" data-tooltip-html={t("tradingModal.reservationFeeTooltip")} />
                          <Tooltip id="tooltip-reservation-fee" variant="dark" style={{ maxWidth: '300px' }}/>
                        </div>
                      </div>
                    </div>
                  </div>}

                  {additionalCostsLabel && <div className="text-xs text-left text-gray-600">
                    {additionalCostsLabel}
                  </div>}
              </div>

              {hasMarketData && requiresConfirmationCheck && (
                  <div className="max-w-[min(84vw,_375px)]">
                      <hr className="my-2"/>
                      <div className="mb-3 font-normal leading-none">
                          <span className="text-xs font-semibold">{ confirmationCheckInfo.title } </span>
                          <span className="text-xs">{ confirmationCheckInfo.info  }</span>
                      </div>
                      <Checkbox
                          label={confirmationCheckInfo.label}
                          checked={isConfirmationChecked}
                          onChange={setConfirmationIsChecked}
                          id="terms-checkbox"
                      />
                  </div>
              )}

              {actionButton}
          </div>}
          {hasMarketData && <DisclaimerBlock keyPrefix={keyPrefix} translationDisclaimerKeys={translationDisclaimerKeys} />}
      </div>
  );
};
